import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { CommonApiService } from './common-api.service';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class DynamicOptionsService {
  private state: { [key: string]: any } = {};

  constructor(private api: CommonApiService) {}

  private initializeState(dropdownKey: string) {
    if (!this.state[dropdownKey]) {
      this.state[dropdownKey] = {
        optionsSubject: new BehaviorSubject<any[]>([]),
        isLoadingSubject: new BehaviorSubject<boolean>(false),
        allOptionsLoadedSubject: new BehaviorSubject<boolean>(false),
        pageIndex: environment.defaultPageIndexOnLoad,
        pageSize: environment.defaultPageSizeOnLoad,
        endpoint: '',
        searchString: '',
        idField: 'id',
      };
    }
  }

  init(
    dropdownKey: string,
    endpoint: string,
    size: number = 10,
    searchString: string = '',
    idField: string = 'id'
  ) {
    this.initializeState(dropdownKey);
    const state = this.state[dropdownKey];
    state.pageIndex = environment.defaultPageIndexOnLoad;
    state.pageSize = size;
    state.endpoint = endpoint;
    state.searchString = searchString;
    state.idField = idField || 'id';
    state.allOptionsLoadedSubject.next(false);
    state.optionsSubject.next([]);
    this.loadOptions(dropdownKey);
  }

  private mergeUnique(dropdownKey: string, incoming: any[]) {
    const state = this.state[dropdownKey];
    const idField = state.idField || 'id';
    const current = state.optionsSubject.value || [];
    const byId = new Map<string, any>();
    const order: string[] = [];
    for (const item of current) {
      const key = String(item?.[idField]);
      if (!byId.has(key)) {
        order.push(key);
      }
      byId.set(key, item);
    }
    for (const item of incoming) {
      const key = String(item?.[idField]);
      if (!byId.has(key)) {
        order.push(key);
      }
      byId.set(key, item);
    }
    state.optionsSubject.next(order.map((k) => byId.get(k)));
  }

  private loadOptions(dropdownKey: string) {
    const state = this.state[dropdownKey];
    state.isLoadingSubject.next(true);
    const params: any = {
      offset: state.pageIndex,
      limit: state.pageSize,
      active: true,
    };
    if (state.searchString !== '' && state.searchString) {
      params.filter = state.searchString;
      params.search = typeof state.searchString === 'string' ? state.searchString : '';
    }
    this.api
      .postAll(state.endpoint + '/list/display', params)
      .subscribe((res: any) => {
        const items = res.items || [];
        if (items.length < state.pageSize) {
          state.allOptionsLoadedSubject.next(true);
        }
        this.mergeUnique(dropdownKey, items);
        state.isLoadingSubject.next(false);
      });
  }

  loadNext(dropdownKey: string) {
    const state = this.state[dropdownKey];
    if (
      state.isLoadingSubject.value ||
      state.allOptionsLoadedSubject.value
    ) {
      return;
    }
    state.pageIndex++;
    this.loadOptions(dropdownKey);
  }

  search(
    dropdownKey: string,
    searchString: string,
    columnName: string = 'name'
  ) {
    const state = this.state[dropdownKey];
    state.pageIndex = environment.defaultPageIndexOnLoad;
    state.allOptionsLoadedSubject.next(false);
    state.optionsSubject.next([]);
    state.searchString = searchString;
    this.loadOptions(dropdownKey);
  }

  getOptions$(dropdownKey: string): Observable<any[]> {
    this.initializeState(dropdownKey);
    return this.state[dropdownKey].optionsSubject.asObservable();
  }

  getIsLoading$(dropdownKey: string): Observable<boolean> {
    this.initializeState(dropdownKey);
    return this.state[dropdownKey].isLoadingSubject.asObservable();
  }

  getAllOptionsLoaded$(dropdownKey: string): Observable<boolean> {
    this.initializeState(dropdownKey);
    return this.state[dropdownKey].allOptionsLoadedSubject.asObservable();
  }

  ensureIncludes(dropdownKey: string, option: any, idField?: string): void {
    if (!option) return;
    this.initializeState(dropdownKey);
    const state = this.state[dropdownKey];
    const key = idField || state.idField || 'id';
    this.mergeUnique(dropdownKey, [option]);
    const current = state.optionsSubject.value || [];
    const idx = current.findIndex(
      (o: any) => String(o?.[key]) === String(option?.[key])
    );
    if (idx > 0) {
      const next = [...current];
      const [item] = next.splice(idx, 1);
      next.unshift(item);
      state.optionsSubject.next(next);
    }
  }
}
